import { CommissionAccompanimentData } from "@/types/commissionAccompaniment";

/**
 * Formata um valor monetário para exibição
 * 
 * @param value - Valor a ser formatado
 * @returns String formatada como moeda brasileira
 */
export const formatCurrency = (value: number): string => {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

/**
 * Formata um valor percentual para exibição
 * 
 * @param value - Valor a ser formatado
 * @returns String formatada como percentual
 */
export const formatPercent = (value: number): string => {
  return `${value.toFixed(2)}%`;
};

/**
 * Gera dados para exportação no formato CSV
 * 
 * @param data - Dados do acompanhamento de comissão
 * @returns Array de arrays contendo os dados formatados para CSV
 */
export const generateCSVData = (data: CommissionAccompanimentData): string[][] => {
  // Cabeçalhos
  const headers = [
    'Vendedor',
    'Total Vendas',
    'Comissão Bruta',
    'Participação (%)',
    'Desconto',
    'Comissão Final'
  ];
  
  // Linhas de dados
  const rows = data.data.map(item => [
    item.seller_name,
    item.total_sales.toString(),
    item.commission_without_discount.toString(),
    item.participation_percent.toString(),
    item.discount.toString(),
    item.commission_final.toString()
  ]);
  
  // Linha de totais
  const totalsRow = [
    'TOTAL',
    '',
    data.summary.commission_total.toString(),
    '100.00',
    data.summary.total_discount.toString(),
    data.summary.commission_final_total.toString()
  ];
  
  return [headers, ...rows, totalsRow];
};

/**
 * Prepara um objeto com os dados agrupados para relatórios
 * 
 * @param data - Dados do acompanhamento de comissão
 * @returns Objeto com dados formatados para relatórios
 */
export const prepareReportData = (data: CommissionAccompanimentData) => {
  return {
    title: `Acompanhamento de Comissão - ${data.commission_name}`,
    headers: [
      'Vendedor',
      'Total Vendas',
      'Comissão Bruta',
      'Participação (%)',
      'Desconto',
      'Comissão Final'
    ],
    rows: data.data.map(item => ({
      seller_name: item.seller_name,
      total_sales: formatCurrency(item.total_sales),
      commission_without_discount: formatCurrency(item.commission_without_discount),
      participation_percent: formatPercent(item.participation_percent),
      discount: formatCurrency(item.discount),
      commission_final: formatCurrency(item.commission_final),
      raw: item
    })),
    summary: {
      commission_total: formatCurrency(data.summary.commission_total),
      total_discount: formatCurrency(data.summary.total_discount),
      percent_discount: formatPercent(data.summary.percent_discount),
      commission_final_total: formatCurrency(data.summary.commission_final_total),
      raw: data.summary
    }
  };
};

/**
 * Exporta os dados para download como um arquivo CSV
 * 
 * @param data - Dados do acompanhamento de comissão
 * @param filename - Nome do arquivo a ser baixado
 */
export const exportToCSV = (data: CommissionAccompanimentData, filename: string = 'acompanhamento-comissao'): void => {
  const csvData = generateCSVData(data);
  const csvContent = csvData.map(row => row.join(';')).join('\n');
  
  // Cria um objeto Blob com os dados CSV
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  
  // Cria um link para download e clica nele
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}; 